import ButtonDefault from 'ui/src/buttons/ButtonDefault'
import styles from 'ui/src/styles/components/parts/Input.module.scss'
import { useEffect, useState } from 'react'
import { useLocale } from '@/features/i18n/hooks/useLocale'
import { apiUrls } from '@/libs/apiUrls'
import { useAuth } from '@/components/providers/AuthProvider'

interface Props {
  className?: string
  isOpen?: boolean
  onClose?: () => void
}

interface Inputs {
  current: string
  new: string
  confirm: string
}

interface Errors {
  current?: string
  new?: string
  confirm?: string
}

export default function TableModalPasswordEdit({ className, isOpen, onClose }: Props) {
  const { t } = useLocale()
  const { token, tUser } = useAuth()

  const [inputs, setInputs] = useState<Inputs>({
    current: '',
    new: '',
    confirm: '',
  })
  const [errors, setErrors] = useState<Errors>({})

  useEffect(() => {
    // モーダルを表示したときに入力欄とエラー表示をリセット
    if (isOpen) {
      setInputs({ current: '', new: '', confirm: '' })
      setErrors({ current: undefined, new: undefined, confirm: undefined })
    }
  }, [isOpen])

  const checkConfirmInput = () => {
    if (inputs.new && inputs.confirm && inputs.new !== inputs.confirm) {
      setErrors((errors) => ({ ...errors, confirm: t.validate.equalTo }))
    } else {
      setErrors((errors) => ({ ...errors, confirm: '' }))
    }
  }
  const saveAction = async () => {
    let newErrors: Errors = {}
    if (!inputs.current) {
      newErrors.current = t.validate.required
    }
    if (!inputs.new) {
      newErrors.new = t.validate.required
    }
    if (!inputs.confirm) {
      newErrors.confirm = t.validate.required
    }
    if (inputs.new && inputs.confirm && inputs.new !== inputs.confirm) {
      newErrors.confirm = t.validate.equalTo
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors)
      return
    }
    setErrors({})

    const data = {
      userPasswordOld: inputs.current,
      userPasswordNew: inputs.new,
      id: tUser.id,
      token: token,
    }

    try {
      const response = await fetch(apiUrls.user.changePassword(), {
        method: 'post',
        body: JSON.stringify(data),
        headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
        cache: 'no-store',
      })
      if (response.ok) {
        const result = await response.json()
        switch (result.status) {
          case 1: // 成功
            onClose?.()
            setErrors({})
            return
          default:
          // console.log(result.msg)
        }
      }
    } catch {}

    setErrors((errors) => ({
      ...errors,
      confirm: t.header.PasswordChangeFailed,
    }))
  }

  return (
    <>
      <table className="mb-5">
        <tbody>
          <tr>
            <th className="font-bold">
              <div className="flex gap-1 items-center">
                {t.header.OldPassword}
                <span className={styles.input__must}>{t.validate.requiredLabel}</span>
              </div>
            </th>
          </tr>
          <tr>
            <td>
              <input
                type="password"
                className={`${styles.inputText} ${styles.inputText__l} ${styles.gray}`}
                placeholder={t.header.OldPasswordPlaceholder}
                value={inputs.current}
                onChange={(e) => setInputs((inputs) => ({ ...inputs, current: e.target.value }))}
              />
              <span className={styles.error}>{errors.current}</span>
            </td>
          </tr>
          <tr>
            <th className="font-bold">
              <div className="flex gap-1 items-center">
                {t.header.NewPassword}
                <span className={styles.input__must}>{t.validate.requiredLabel}</span>
              </div>
            </th>
          </tr>
          <tr>
            <td>
              <input
                type="password"
                className={`${styles.inputText} ${styles.inputText__l} ${styles.gray}`}
                placeholder={t.header.NewPasswordPlaceholder}
                value={inputs.new}
                onChange={(e) => setInputs((inputs) => ({ ...inputs, new: e.target.value }))}
                onBlur={checkConfirmInput}
              />
              <span className={styles.error}>{errors.new}</span>
            </td>
          </tr>
          <tr>
            <th className="font-bold">
              <div className="flex gap-1 items-center">
                {t.header.ConfirmPassword}
                <span className={styles.input__must}>{t.validate.requiredLabel}</span>
              </div>
            </th>
          </tr>
          <tr>
            <td>
              <input
                type="password"
                className={`${styles.inputText} ${styles.inputText__l} ${styles.gray}`}
                placeholder={t.header.NewPasswordPlaceholder}
                value={inputs.confirm}
                onChange={(e) => setInputs((inputs) => ({ ...inputs, confirm: e.target.value }))}
                onBlur={checkConfirmInput}
              />
              <span className={styles.error}>{errors.confirm}</span>
            </td>
          </tr>
        </tbody>
      </table>
      <hr className="hr mb-7" />
      <div className="flex gap-5 justify-center">
        <ButtonDefault label={t.button.Cancel} color="secondary" action={onClose} />
        <ButtonDefault label={t.button.Save} color="primary" action={saveAction} />
      </div>
    </>
  )
}
